import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { BidSimulationFailedError, useSimulateBids } from "../../../../modules/advertising/api/useSimulateBids";
import { useDeploySimulatedBids } from "../../../../modules/advertising/api/useDeploySimulatedBids";
import { useBulkUpdateAdPropertiesV2 } from "../../../../modules/advertising/api/useBulkUpdateAdPropertiesV2";
import type { SimulationRow } from "../../../../zodios/apiDefinitions/api/bidSimulation";
import type { SimulationParameters } from "../../../../modules/advertising/components/BidSimulation/SimulationParametersForm";
import type { BulkAdPropertyUpdate } from "../../../../zodios/apiDefinitions/api/ads";
import { roasToAcos } from "../../../core/blocks/AdPlan/utils";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { errorToast, successToast } from "../../../../utils/toasts";

type AdPlanParams = {
  targetACOS?: number;
  maxACOS?: number;
  dailyBudget?: number;
  minBid?: number | null;
  maxBid?: number | null;
  algorithm?: string;
};

function isNum(v: unknown): v is number {
  return typeof v === "number" && !isNaN(v);
}

function buildSimulationApiParams(formValues: SimulationParameters, isRoas: boolean) {
  const params: Record<string, unknown> = {};

  if (formValues.algorithm) params.algorithm = formValues.algorithm;

  if (isNum(formValues.min_bid) || isNum(formValues.max_bid)) {
    params.guardrails = {
      ...(isNum(formValues.min_bid) ? { min_bid: formValues.min_bid } : {}),
      ...(isNum(formValues.max_bid) ? { max_bid: formValues.max_bid } : {}),
    };
  }

  if (isRoas) {
    if (isNum(formValues.min_roas)) params.max_acos = roasToAcos(formValues.min_roas);
    if (isNum(formValues.target_roas)) params.min_acos = roasToAcos(formValues.target_roas);
  } else {
    if (isNum(formValues.max_acos)) params.max_acos = formValues.max_acos;
    if (isNum(formValues.min_acos)) params.min_acos = formValues.min_acos;
  }

  if (isNum(formValues.daily_budget)) params.daily_budget = formValues.daily_budget;

  return params;
}

function buildAdPlanUpdates(params: SimulationParameters, isRoas: boolean): BulkAdPropertyUpdate["updates"] {
  const updates: BulkAdPropertyUpdate["updates"] = [];

  if (isRoas) {
    if (isNum(params.min_roas) && params.min_roas !== 0)
      updates.push({ field: "min_roas", action: "set", value: params.min_roas });
    if (isNum(params.target_roas) && params.target_roas !== 0)
      updates.push({ field: "target_roas", action: "set", value: params.target_roas });
  } else {
    if (isNum(params.max_acos)) updates.push({ field: "max_acos", action: "set", value: params.max_acos });
    if (isNum(params.min_acos)) updates.push({ field: "target_acos", action: "set", value: params.min_acos });
  }

  if (isNum(params.daily_budget)) updates.push({ field: "daily_budget", action: "set", value: params.daily_budget });

  return updates;
}

export function useBidSimulation({ adId, adPlanParams }: { adId: number; adPlanParams: AdPlanParams }) {
  const { t } = useTranslation(["advertising"]);
  const acosOrRoas = useAcosOrRoas();
  const isRoas = acosOrRoas(false, true);

  const [simulationMode, setSimulationMode] = useState(false);
  const [simulationResults, setSimulationResults] = useState<SimulationRow[]>([]);
  const [lastAppliedParams, setLastAppliedParams] = useState<SimulationParameters | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const simulateMutation = useSimulateBids();
  const { reset: resetSimulateMutation } = simulateMutation;
  const deployMutation = useDeploySimulatedBids();
  const bulkUpdateMutation = useBulkUpdateAdPropertiesV2();

  const simulationDataMap = useMemo(() => {
    const map = new Map<string, SimulationRow>();
    for (const row of simulationResults) {
      map.set(row.target_id, row);
    }
    return map;
  }, [simulationResults]);

  const simulationInitialValues = useMemo<Partial<SimulationParameters>>(() => {
    const values: Partial<SimulationParameters> = {};
    if (adPlanParams.algorithm) values.algorithm = adPlanParams.algorithm;
    if (isNum(adPlanParams.minBid)) values.min_bid = adPlanParams.minBid;
    if (isNum(adPlanParams.maxBid)) values.max_bid = adPlanParams.maxBid;
    if (isNum(adPlanParams.dailyBudget)) values.daily_budget = adPlanParams.dailyBudget;
    if (isRoas) {
      if (isNum(adPlanParams.maxACOS) && adPlanParams.maxACOS > 0) values.min_roas = adPlanParams.maxACOS;
      if (isNum(adPlanParams.targetACOS) && adPlanParams.targetACOS > 0) values.target_roas = adPlanParams.targetACOS;
    } else {
      if (isNum(adPlanParams.maxACOS)) values.max_acos = adPlanParams.maxACOS;
      if (isNum(adPlanParams.targetACOS)) values.min_acos = adPlanParams.targetACOS;
    }
    return values;
  }, [adPlanParams, isRoas]);

  const handleApplySimulation = useCallback(
    (formValues: SimulationParameters) => {
      const params = buildSimulationApiParams(formValues, isRoas);

      abortRef.current = new AbortController();

      simulateMutation.mutate(
        { advertisement_id: adId, parameters: params, signal: abortRef.current.signal },
        {
          onSuccess: (data) => {
            setSimulationResults(data);
            setLastAppliedParams(formValues);
          },
          onError: (error) => {
            let message: string | undefined;
            if (error instanceof BidSimulationFailedError) {
              message = error.message;
            } else if (isAxiosError<{ error?: string } | null>(error) && error.response?.status === 422) {
              message = error.response.data?.error;
            }
            errorToast({
              title: t(($) => $.advertising["Simulation failed"]),
              message: message || t(($) => $.advertising["Failed to run simulation. Please try again."]),
              error,
            });
          },
        },
      );
    },
    [adId, isRoas, simulateMutation, t],
  );

  const handleDeploy = useCallback(async () => {
    if (simulationResults.length === 0) return;

    const overrides = simulationResults.map((row) => ({
      target_id: row.target_id,
      type: row.type,
      new_bid: row.new_bid,
    }));

    try {
      await deployMutation.mutateAsync({ advertisement_id: adId, overrides });

      if (lastAppliedParams) {
        const updates = buildAdPlanUpdates(lastAppliedParams, isRoas);
        if (updates.length > 0) {
          await bulkUpdateMutation.mutateAsync({
            advertisements: [adId],
            updates,
          });
        }
      }

      successToast({ message: t(($) => $.advertising["Simulated bids applied successfully"]) });
      setSimulationMode(false);
      setSimulationResults([]);
      setLastAppliedParams(null);
    } catch (error) {
      errorToast({
        title: t(($) => $.advertising["Apply failed"]),
        message: t(($) => $.advertising["Failed to apply simulated bids. Please try again."]),
        error,
      });
    }
  }, [adId, simulationResults, lastAppliedParams, isRoas, deployMutation, bulkUpdateMutation, t]);

  const enterSimulation = useCallback(() => setSimulationMode(true), []);

  const exitSimulation = useCallback(() => {
    setSimulationMode(false);
    setSimulationResults([]);
    setLastAppliedParams(null);
    abortRef.current?.abort();
    resetSimulateMutation();
  }, [resetSimulateMutation]);

  useEffect(() => exitSimulation, [adId, exitSimulation]);

  return {
    simulationMode,
    enterSimulation,
    exitSimulation,
    simulationDataMap,
    simulationInitialValues,
    handleApplySimulation,
    handleDeploy,
    isSimulating: simulateMutation.isPending,
    isDeploying: deployMutation.isPending,
    hasResults: simulationResults.length > 0,
  };
}
